import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/customer/Header';
import Footer from '../../components/customer/Footer';
import Home2 from '../../components/customer/Home2';
import api from '../../utils/api';
import './HomeAlternative2.css';

const HomeAlternative2 = () => {
  const navigate = useNavigate();
  const [sections, setSections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [animatedElements, setAnimatedElements] = useState(new Set());

  const heroRef = useRef(null);
  const featuresRef = useRef(null);
  const gridRef = useRef(null);
  const ctaRef = useRef(null);
  const featuredRef = useRef(null);

  useEffect(() => {
    fetchSections();
  }, []);

  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Intersection Observer for scroll animations
  useEffect(() => {
    const observerOptions = {
      threshold: 0.1,
      rootMargin: '0px 0px -50px 0px'
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting && !animatedElements.has(entry.target)) {
          entry.target.classList.add('animate-in');
          setAnimatedElements(prev => new Set([...prev, entry.target]));
        }
      });
    }, observerOptions);

    const elements = [heroRef.current, featuresRef.current, gridRef.current, ctaRef.current, featuredRef.current];
    elements.forEach(el => el && observer.observe(el));

    return () => observer.disconnect();
  }, [sections]);

  const fetchSections = async () => {
    try {
      const response = await api.get('/home/alternative-2');
      if (response.data.success) {
        setSections(response.data.data);
      }
    } catch (error) {
      console.error('Failed to fetch sections:', error);
    } finally {
      setLoading(false);
    }
  };

  const isMobile = windowWidth < 768;
  const isTablet = windowWidth >= 768 && windowWidth < 1024;

  const heroSplit = sections.find(s => s.section === 'hero_split');
  const featureLeft = sections.find(s => s.section === 'feature_left');
  const featureRight = sections.find(s => s.section === 'feature_right');
  const gridItems = sections.filter(s => s.section.startsWith('grid_'));
  const ctaBanner = sections.find(s => s.section === 'cta_banner');

  if (loading) {
    return (
      <div className="home-alt-2-container">
        <div className="loading-screen">
          <div className="spinner"></div>
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="home-alt-2-container">
      {/* Hero Split Section */}
      {heroSplit && (
        <div ref={heroRef} className="hero-split-section fade-in-up">
          <div className="hero-split-content">
            <div className="hero-split-text">
              <span className="hero-split-subtitle">{heroSplit.subtitle}</span>
              <h1 className="hero-split-title">{heroSplit.title}</h1>
              <p className="hero-split-description">{heroSplit.description}</p>
              <button
                className="hero-split-button"
                onClick={() => navigate(heroSplit.button_link)}
              >
                {heroSplit.button_text}
                <span className="button-arrow">→</span>
              </button>
            </div>
            <div className="hero-split-image">
              <img
                src={heroSplit.image_url.startsWith('http') ? heroSplit.image_url : `https://kid-o.ge${heroSplit.image_url}`}
                alt={heroSplit.title}
              />
            </div>
          </div>
        </div>
      )}

      {/* Feature Sections */}
      {(featureLeft || featureRight) && (
        <div ref={featuresRef} className="features-section fade-in-up">
          {featureLeft && (
            <FeatureCard section={featureLeft} navigate={navigate} align="left" />
          )}
          {featureRight && (
            <FeatureCard section={featureRight} navigate={navigate} align="right" />
          )}
        </div>
      )}

      {/* Grid Section */}
      {gridItems.length > 0 && (
        <div ref={gridRef} className="grid-section fade-in-up">
          <div className="grid-container">
            {gridItems.map((item, index) => (
              <GridCard key={item.id} section={item} navigate={navigate} index={index} />
            ))}
          </div>
        </div>
      )}

      {/* CTA Banner */}
      {ctaBanner && (
        <div 
          ref={ctaRef} 
          className="cta-banner fade-in-up"
          style={{ backgroundColor: ctaBanner.background_color }}
        >
          <div className="cta-content">
            <h2 className="cta-title" style={{ color: ctaBanner.text_color }}>
              {ctaBanner.title}
            </h2>
            <p className="cta-subtitle" style={{ color: ctaBanner.text_color }}>
              {ctaBanner.subtitle}
            </p>
            <p className="cta-description" style={{ color: ctaBanner.text_color }}>
              {ctaBanner.description}
            </p>
            <button
              className="cta-button"
              onClick={() => navigate(ctaBanner.button_link)}
            >
              {ctaBanner.button_text}
            </button>
          </div>
        </div>
      )}

      {/* Featured Carousel */}
      <div ref={featuredRef} className="featured-section fade-in-up">
        <Home2 />
      </div>
    </div>
  );
};

// Feature Card Component
const FeatureCard = ({ section, navigate, align }) => {
  const [isHovered, setIsHovered] = useState(false);

  return (
    <div 
      className={`feature-card feature-card-${align}`}
      style={{ backgroundColor: section.background_color }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <div className="feature-image">
        <img
          src={section.image_url.startsWith('http') ? section.image_url : `https://kid-o.ge${section.image_url}`}
          alt={section.title}
          style={{ transform: isHovered ? 'scale(1.1)' : 'scale(1)' }}
        />
      </div>
      <div className="feature-text" style={{ color: section.text_color }}>
        <span className="feature-subtitle">{section.subtitle}</span>
        <h3 className="feature-title">{section.title}</h3>
        <p className="feature-description">{section.description}</p>
        <button
          className="feature-button"
          onClick={() => navigate(section.button_link)}
          style={{
            color: section.text_color,
            borderColor: section.text_color
          }}
        >
          {section.button_text}
        </button>
      </div>
    </div>
  );
};

// Grid Card Component
const GridCard = ({ section, navigate, index }) => {
  const [isHovered, setIsHovered] = useState(false);

  return (
    <div
      className={`grid-card grid-card-${index + 1}`}
      style={{ backgroundColor: section.background_color }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onClick={() => navigate(section.button_link)}
    >
      <div className="grid-card-image">
        <img
          src={section.image_url.startsWith('http') ? section.image_url : `https://kid-o.ge${section.image_url}`}
          alt={section.title}
          style={{ transform: isHovered ? 'scale(1.1)' : 'scale(1)' }}
        />
      </div>
      <div className="grid-card-overlay" style={{ 
        background: isHovered 
          ? 'linear-gradient(to top, rgba(0,0,0,0.9), rgba(0,0,0,0.3))' 
          : 'linear-gradient(to top, rgba(0,0,0,0.7), transparent)' 
      }}>
        <div className="grid-card-content">
          <span className="grid-card-subtitle">{section.subtitle}</span>
          <h4 className="grid-card-title">{section.title}</h4>
          <span className="grid-card-button">{section.button_text} →</span>
        </div>
      </div>
    </div>
  );
};

export default HomeAlternative2;